/// EVENTS ///
import { SyntheticEvent } from "react";
import { MouseEvent as ReactMouseEvent } from "react";
/// UUID ///
import { v4 } from "uuid";
/// HOOKS ///
import { useState } from "react";
import { useEffect } from "react";
import { useAppDispatch } from "api/hooks/apiHook";
import { useAppSelector } from "api/hooks/apiHook";
import useMousePosition from "hooks/mouse_position";
import useDraw from "hooks/draw";
/// HANDLERS ///
import { handleCreateCounter } from "handler/counter/create";
/// ACTIONS ///
import { createLabel } from "api/actions/sketch/label";
import { createCooktop } from "api/actions/sketch/cooktop";
import { updateCooktop } from "api/actions/sketch/cooktop";
import { deleteCooktop } from "api/actions/sketch/cooktop";
import { updateLocalCooktop } from "api/actions/sketch/cooktop";
import { createRestorePoint } from "api/actions/history";
import { createSink } from "api/actions/sketch/sink";
import { updateSink } from "api/actions/sketch/sink";
import { deleteSink } from "api/actions/sketch/sink";
import { createLocalSink } from "api/actions/sketch/sink";
import { updateLocalSink } from "api/actions/sketch/sink";
import { deleteLocalSink } from "api/actions/sketch/sink";
import { markQuoteChanged } from "api/actions/quotes/quote";
import { deleteLabel } from "api/actions/sketch/label";
import { updateLabel } from "api/actions/sketch/label";
import { updateLocalLabel } from "api/actions/sketch/label";
/// TYPES ///
import { Counter } from "api/types/sketch";
import { Cooktop } from "api/types/sketch";
import { HighlightData } from "types";
import { Label } from "api/types/sketch";
import { IHistoryActionType } from "api/types/history";
import { MouseData } from "types/mouse_position";
import { Slab } from "api/types/quote";
import { Sink } from "api/types/sketch";
import { Vector2 } from "api/types/sketch";
/// MUI ///
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
/// COMPONENTS ///
import AppliancesMenu from "components/quote/menu/appliances";
import StoreAreaBar from "../bar/area";
import CooktopCutoutMenu from "components/quote/menu/cooktop_cutout";
import SinkCutoutMenu from "components/quote/menu/sink_cutout";
import LabelMenu from "components/quote/menu/label";
import CreateStoreCooktop from "../drawer/create_cooktop";
import UpdateStoreCooktop from "../drawer/update_cooktop";
import CreateStoreSink from "../drawer/create_sink";
import UpdateStoreSink from "../drawer/update_sink";
import UpdateStoreLabel from "../dialog/label/update";
import UpdateStoreOutlets from "../drawer/update_outlet";
/// STYLES ///
import { sketch_style } from "styles/sketch";
import { canvasStyle } from "styles/sketch";
/// FUNCTIONS ///
import { getMouseAtZoom } from "functions/sketch";
import { duplicateCounter } from "functions/sketch";
import { getFirstSlabInAreas } from "functions/sketch/get/slab";
import { moveSink } from "functions/sketch";
import { moveCooktop } from "functions/sketch";
import { checkCounterClicked } from "functions/sketch/check";
import { checkCooktopClicked } from "functions/sketch/check";
import { checkIndicatorSelected } from "functions/sketch/check/indicator";
import { checkSinkClicked } from "functions/sketch/check";
import { moveVector } from "functions/sketch/move/vector";
/// EMPTY VALUES ///
import { empty_sink } from "values/empty/quote/sink";
import { empty_cooktop } from "values/empty/quote/cooktop";
/// VALUES ///
import { getCopyMessage } from "values/text";
import { handleDeleteCounter } from "handler/counter/delete";

interface Props {
  index: number,
  zoom: number,
  current_area: number,
  setCurrentArea: (area: number) => void,
  copying: boolean,
  setCopying: (copying: boolean) => void
}

export default function StoreAppliances({ index, zoom, current_area, setCurrentArea, copying, setCopying }: Props) {
  const dispatch = useAppDispatch();
  const [selected_counter, setSelectedCounter] = useState<Counter | null>(null);
  const mouse_data: MouseData = useMousePosition();
  const ref = mouse_data.ref;
  const [selected_cooktop, setSelectedCooktop] = useState<Cooktop | null>(null);
  const [selected_sink, setSelectedSink] = useState<Sink | null>(null);
  const { quote } = useAppSelector(state => state.quote);
  const { areas } = useAppSelector(state => state.quote);
  const { counters } = useAppSelector(state => state.sketch);
  const { cut_shapes } = useAppSelector(state => state.sketch);
  const { labels } = useAppSelector(state => state.sketch);
  const [selected_label, setSelectedLabel] = useState<Label | null>(null);
  const [lifting_label, setLiftingLabel] = useState<boolean>(false);
  const [lifting, setLifting] = useState<boolean>(false);
  const [lift_point, setLiftPoint] = useState<Vector2>({ X: 0, Y: 0 });
  const [in_operation, setInOperation] = useState<boolean>(false);
  const [appliance_menu_open, setApplianceMenuOpen] = useState<boolean>(false);
  const [sink_menu_open, setSinkMenuOpen] = useState<boolean>(false);
  const [cooktop_menu_open, setCooktopMenuOpen] = useState<boolean>(false);
  const [label_menu_open, setLabelMenuOpen] = useState<boolean>(false);
  const [edit_label_open, setEditLabelOpen] = useState<boolean>(false);
  const [outlet_cutout_open, setOutletCutoutOpen] = useState<boolean>(false);
  const [add_sink_open, setAddSinkOpen] = useState<boolean>(false);
  const [change_sink_open, setChangeSinkOpen] = useState<boolean>(false);
  const [add_cooktop_open, setAddCooktopOpen] = useState<boolean>(false);
  const [change_cooktop_open, setChangeCooktopOpen] = useState<boolean>(false);
  const [menu_location, setMenuLocation] = useState<Vector2>({ X: 0, Y: 0 });
  const [copy, setCopy] = useState<HighlightData | null>(null);

  const highlight_data: HighlightData = useDraw(
    index,
    selected_counter,
    areas[current_area]?.uuid,
    mouse_data,
    add_sink_open || change_sink_open || add_cooktop_open || change_cooktop_open || appliance_menu_open || sink_menu_open || cooktop_menu_open,
    false,
    lifting,
    in_operation,
    zoom,
    {
      show_measurements: true,
      show_appliances: true,
      appliances_selectable: !add_sink_open && !change_sink_open && !add_cooktop_open && !change_cooktop_open && !appliance_menu_open && !sink_menu_open && !cooktop_menu_open,
    }
  );

  const handleEvent = (event: SyntheticEvent) => {
    const mouse_event: MouseEvent | null = event.nativeEvent instanceof MouseEvent ? event.nativeEvent as MouseEvent : null;
    const mouse: Vector2 = getMouseAtZoom(mouse_data.position, zoom);
    if ((mouse_event && mouse_event.type === "mousedown" && mouse_event.button === 0)) {
      if (copying && !copy && highlight_data) {
        setCopy(highlight_data);
        setInOperation(false);
        return;
      }
      else if (copying && copy) {
        if (highlight_data?.cooktop && copy?.cooktop) {
          dispatch(updateCooktop({
            ...highlight_data.cooktop,
            num_burners: copy.cooktop.num_burners,
            length: copy.cooktop.length,
            width: copy.cooktop.width,
            angle: copy.cooktop.angle
          }));
        }
        else if (highlight_data?.sink && copy?.sink) {
          dispatch(updateSink({
            ...highlight_data.sink,
            sink_type: copy.sink.sink_type,
            shape: copy.sink.shape,
            faucet_hole_count: copy.sink.faucet_hole_count,
            length: copy.sink.length,
            width: copy.sink.width,
            angle: copy.sink.angle
          }));
        }
        setInOperation(false);
        return;
      }

      const no_menus_open: boolean = !add_sink_open && !change_cooktop_open && !add_cooktop_open && !change_sink_open && !appliance_menu_open &&
        !cooktop_menu_open && !sink_menu_open;
      if (no_menus_open) {
        for (let i = 0; i < labels.length; i++) {
          if (checkIndicatorSelected(labels[i].location, mouse, true, 55)) {
            setSelectedLabel(labels[i]);
            setLiftingLabel(true);
            return;
          }
        }
      }
      const target: HTMLDivElement = event.target as HTMLDivElement;
      if (appliance_menu_open) {
        if (!target.id.startsWith("menu")) {
          setApplianceMenuOpen(false);
        }
      }
      else if (cooktop_menu_open) {
        if (!target.id.startsWith("menu")) {
          setCooktopMenuOpen(false);
        }
      }
      else if (sink_menu_open) {
        if (!target.id.startsWith("menu")) {
          setSinkMenuOpen(false);
        }
      }
      else if (highlight_data && highlight_data.cooktop) {
        setLifting(true);
        setLiftPoint(mouse);
        setSelectedCooktop(highlight_data.cooktop);
        return;
      }
      else if (highlight_data && highlight_data.sink) {
        setLifting(true);
        setLiftPoint(mouse);
        setSelectedSink(highlight_data.sink);
        return;
      }
    }
    else if ((mouse_event && mouse_event.type === "mouseup" && mouse_event.button === 0)) {
      if (lifting) {
        setLifting(false);
        if (selected_cooktop) {
          const updated_cooktop: Cooktop = { ...selected_cooktop };
          const cooktop: Cooktop = moveCooktop(updated_cooktop, getMouseAtZoom(lift_point, zoom), mouse);
          dispatch(updateCooktop(cooktop, updated_cooktop.id));

          dispatch(createRestorePoint(
            {
              undo_action: IHistoryActionType.UPDATE_COOKTOP,
              redo_action: IHistoryActionType.UPDATE_COOKTOP,
              desc: "Move Cooktop",
              undo_data: updated_cooktop,
              redo_data: cooktop
            }
          ));
        }
        else if (selected_sink) {
          const updated_sink: Sink = { ...selected_sink };
          const sink: Sink = moveSink(updated_sink, getMouseAtZoom(lift_point, zoom), mouse);
          dispatch(updateSink(sink, updated_sink.id));

          dispatch(createRestorePoint(
            {
              undo_action: IHistoryActionType.UPDATE_SINK,
              redo_action: IHistoryActionType.UPDATE_SINK,
              desc: "Move Sink",
              undo_data: updated_sink,
              redo_data: sink
            }
          ));
        }
        setSelectedCooktop(null);
        setSelectedSink(null);
      }
      else if (lifting_label && selected_label) {
        const label: Label = { ...selected_label };
        const vector: Vector2 = moveVector(selected_label.location, mouse);
        dispatch(updateLabel({ ...label, location: vector }, label.id));
        dispatch(updateLocalLabel({ ...label, location: vector }));
        setSelectedLabel(null);
        setLiftingLabel(false);
      }
    }
    else if ((mouse_event && mouse_event.type === "mousedown" && mouse_event.button === 2)) {
      if (copying) {
        setCopying(false);
        setCopy(null);
        setInOperation(false);
        return;
      }
      for (let i = 0; i < labels.length; i++) {
        if (checkIndicatorSelected(labels[i].location, getMouseAtZoom(mouse, zoom), true)) {
          setSelectedLabel(labels[i]);
          setMenuLocation({
            X: mouse_event.pageX,
            Y: mouse_event.pageY
          });
          setLabelMenuOpen(true);
          return;
        }
      }

      const cooktop_clicked: Cooktop | null = checkCooktopClicked(counters.filter(c => c.area_uuid === areas[current_area].uuid), mouse_data.position, zoom)
      if (cooktop_clicked) {
        setSelectedCooktop(cooktop_clicked);
        setCooktopMenuOpen(true);
        setSinkMenuOpen(false);
        setApplianceMenuOpen(false);
        setMenuLocation({
          X: mouse_event.pageX,
          Y: mouse_event.pageY
        });
        return;
      }
      const sink_clicked: Sink | null = checkSinkClicked(counters.filter(c => c.area_uuid === areas[current_area].uuid), mouse_data.position, zoom);
      if (sink_clicked) {
        setSelectedSink(sink_clicked);
        setSinkMenuOpen(true);
        setCooktopMenuOpen(false);
        setApplianceMenuOpen(false);
        setMenuLocation({
          X: mouse_event.pageX,
          Y: mouse_event.pageY
        });
        return;
      }

      const counter: Counter | null = checkCounterClicked(counters.filter(c => c.area_uuid === areas[current_area].uuid), mouse);
      if (counter) {
        setSelectedCounter(counter);
        setMenuLocation({
          X: mouse_event.pageX,
          Y: mouse_event.pageY
        });
        setApplianceMenuOpen(true);
        setSinkMenuOpen(false);
        setCooktopMenuOpen(false);
        return;
      }
    }
  }

  useEffect(() => {
    const mouse: Vector2 = mouse_data.position
    if (lifting) {
      if (selected_cooktop) {
        const cooktop: Cooktop = moveCooktop(selected_cooktop, getMouseAtZoom(lift_point, zoom), getMouseAtZoom(mouse, zoom));
        if (cooktop) {
          dispatch(updateLocalCooktop(cooktop));
        }
      }
      else if (selected_sink) {
        const sink: Sink = moveSink(selected_sink, getMouseAtZoom(lift_point, zoom), getMouseAtZoom(mouse, zoom));
        if (sink) {
          dispatch(updateLocalSink(sink));
        }
      }
    }
    else if (selected_label && lifting_label) {
      const vector: Vector2 = moveVector(selected_label.location, getMouseAtZoom(mouse_data.position, zoom));
      dispatch(updateLocalLabel({ ...selected_label, location: vector }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mouse_data.position]);

  const deleteSelectedCounter = () => {
    setInOperation(true);
    if (selected_counter) {
      const counter_to_delete: Counter = { ...selected_counter };
      const uuid: string = counter_to_delete.uuid;
      const counter_id: number = counter_to_delete.id;
      handleDeleteCounter(
        dispatch,
        setSelectedCounter,
        uuid,
        counter_id,
        { ...selected_counter },
        cut_shapes
      );
    }
    setInOperation(false);
    setApplianceMenuOpen(false);
  }

  const handleDuplicateCounter = () => {
    setInOperation(true);
    if (selected_counter) {
      const counter: Counter = duplicateCounter(selected_counter);
      const slab: Slab | null = getFirstSlabInAreas(areas, counter.area_uuid);
      handleCreateCounter(
        dispatch,
        quote.id,
        counter,
        counter.area_uuid,
        slab.uuid
      );
    }
    setInOperation(false);
    setApplianceMenuOpen(false);
  }

  const handleSinkCutout = () => {
    setMenuLocation(mouse_data.position);
    setSelectedSink({ ...empty_sink, location: menu_location });
    setAddSinkOpen(true);
    setApplianceMenuOpen(false);
  }

  const handleAddCooktopCutout = () => {
    setMenuLocation(mouse_data.position);
    setSelectedCooktop({ ...empty_cooktop, location: menu_location });
    setAddCooktopOpen(true);
    setApplianceMenuOpen(false);
  }

  const handleAddOutletCutout = () => {
    setOutletCutoutOpen(true);
    setApplianceMenuOpen(false);
  }

  const handleOpenSinkProperties = () => {
    setChangeSinkOpen(true);
    setSinkMenuOpen(false);
  }

  const handleRotateSink = (rotation: number) => {
    if (selected_sink) {
      const sink: Sink = { ...selected_sink, angle: (selected_sink.angle + rotation) % 360 };
      setSelectedSink(sink);
      dispatch(updateLocalSink(sink));
      dispatch(updateSink(sink, selected_sink.id, true));

      dispatch(createRestorePoint(
        {
          undo_action: IHistoryActionType.UPDATE_SINK,
          redo_action: IHistoryActionType.UPDATE_SINK,
          desc: "Rotate Sink",
          undo_data: { ...selected_sink },
          redo_data: sink
        }
      ));
    }
  }

  const handleDuplicateSink = () => {
    if (selected_sink) {
      const new_sink: Sink = {
        ...selected_sink,
        uuid: v4(),
        location: {
          X: selected_sink.location.X - 100 > 100 ? selected_sink.location.X - 100 : selected_sink.location.X + 100,
          Y: selected_sink.location.Y - 100 > 100 ? selected_sink.location.Y - 100 : selected_sink.location.Y + 100
        }
      };
      delete new_sink["id"];
      dispatch(createLocalSink(new_sink));
      dispatch(createSink(new_sink));
      dispatch(markQuoteChanged(quote?.id));

      dispatch(createRestorePoint(
        {
          undo_action: IHistoryActionType.DELETE_SINK,
          redo_action: IHistoryActionType.CREATE_SINK,
          desc: "Duplicate Sink",
          undo_data: { uuid: new_sink.uuid, counter_uuid: new_sink.counter_uuid },
          redo_data: new_sink
        }
      ));
    }
    setSinkMenuOpen(false);
  }

  const handleDeleteSink = () => {
    if (selected_sink) {
      const sink: Sink = { ...selected_sink };
      dispatch(deleteLocalSink(sink));
      dispatch(deleteSink(sink, sink.id));
      dispatch(markQuoteChanged(quote?.id));

      dispatch(createRestorePoint(
        {
          undo_action: IHistoryActionType.CREATE_SINK,
          redo_action: IHistoryActionType.DELETE_SINK,
          desc: "Delete Sink",
          undo_data: sink,
          redo_data: { uuid: sink.uuid, counter_uuid: sink.counter_uuid }
        }
      ));
    }
    setSinkMenuOpen(false);
  }

  const handleOpenCooktopProperties = () => {
    setChangeCooktopOpen(true);
    setCooktopMenuOpen(false);
  }

  const handleRotateCooktop = (rotation: number) => {
    if (selected_cooktop) {
      const cooktop: Cooktop = { ...selected_cooktop, angle: (selected_cooktop.angle + rotation) % 360 };
      setSelectedCooktop(cooktop);
      dispatch(updateLocalCooktop(cooktop));
      dispatch(updateCooktop(cooktop, selected_cooktop.id, true));

      dispatch(createRestorePoint(
        {
          undo_action: IHistoryActionType.UPDATE_COOKTOP,
          redo_action: IHistoryActionType.UPDATE_COOKTOP,
          desc: "Rotate Cooktop",
          undo_data: { ...selected_cooktop },
          redo_data: cooktop
        }
      ));
    }
  }

  const handleDuplicateCooktop = () => {
    if (selected_cooktop) {
      const new_cooktop: Cooktop = {
        ...selected_cooktop,
        uuid: v4(),
        location: {
          X: selected_cooktop.location.X - 100 > 100 ? selected_cooktop.location.X - 100 : selected_cooktop.location.X + 100,
          Y: selected_cooktop.location.Y - 100 > 100 ? selected_cooktop.location.Y - 100 : selected_cooktop.location.Y + 100
        }
      };
      delete new_cooktop["id"];
      dispatch(createCooktop(new_cooktop));
      dispatch(markQuoteChanged(quote?.id));

      dispatch(createRestorePoint(
        {
          undo_action: IHistoryActionType.DELETE_COOKTOP,
          redo_action: IHistoryActionType.CREATE_COOKTOP,
          desc: "Duplicate Cooktop",
          undo_data: { uuid: new_cooktop.uuid, counter_uuid: new_cooktop.counter_uuid },
          redo_data: new_cooktop
        }
      ));
    }
    setCooktopMenuOpen(false);
  }

  const handleDeleteCooktop = () => {
    if (selected_cooktop) {
      const cooktop: Cooktop = { ...selected_cooktop };

      dispatch(deleteCooktop(cooktop, cooktop.id));
      dispatch(markQuoteChanged(quote?.id));
      dispatch(createRestorePoint(
        {
          undo_action: IHistoryActionType.CREATE_COOKTOP,
          redo_action: IHistoryActionType.DELETE_COOKTOP,
          desc: "Delete Cooktop",
          undo_data: cooktop,
          redo_data: { uuid: cooktop.uuid, counter_uuid: cooktop.counter_uuid }
        }
      ));
    }
    setCooktopMenuOpen(false);
  }

  const handleOpenEditLabel = () => {
    setEditLabelOpen(true);
    setLabelMenuOpen(false);
  }

  const duplicateLabel = () => {
    if (selected_label) {
      dispatch(createLabel({ text: selected_label.text, area: selected_label.area, size: selected_label.size, location: { X: 450, Y: 450 }, quote: quote.id }));
    }
    setLabelMenuOpen(false);
  }

  const handleDeleteLabel = () => {
    const id: number = selected_label.id;
    dispatch(deleteLabel(id));
    setLabelMenuOpen(false);
  }

  return (
    <div>
      <StoreAreaBar current_area={current_area} setArea={setCurrentArea} />
      <Box onContextMenu={(e) => { e.preventDefault(); }}
        sx={sketch_style(copy ? "crosshair" : copying ? "grab" : "normal")}
        onMouseDown={(event: ReactMouseEvent) => handleEvent(event)}
        onMouseUp={(event: ReactMouseEvent) => handleEvent(event)}
        ref={ref}>
        <AppliancesMenu
          open={appliance_menu_open}
          menu_location={menu_location}
          deleteCounter={deleteSelectedCounter}
          duplicateCounter={handleDuplicateCounter}
          addSinkCutout={handleSinkCutout}
          addCooktopCutout={handleAddCooktopCutout}
          addOutletCutout={handleAddOutletCutout} />
        <SinkCutoutMenu
          open={sink_menu_open}
          menu_location={menu_location}
          openProperties={handleOpenSinkProperties}
          handleRotateSink={handleRotateSink}
          duplicateSink={handleDuplicateSink}
          deleteSink={handleDeleteSink} />
        <CooktopCutoutMenu
          open={cooktop_menu_open}
          menu_location={menu_location}
          openProperties={handleOpenCooktopProperties}
          rotateCooktop={handleRotateCooktop}
          duplicateCooktop={handleDuplicateCooktop}
          deleteCooktop={handleDeleteCooktop} />
        <LabelMenu
          open={label_menu_open}
          menu_location={menu_location}
          openEditLabel={handleOpenEditLabel}
          duplicateLabel={duplicateLabel}
          deleteLabel={handleDeleteLabel} />
        <canvas id="canvas" className={JSON.stringify(canvasStyle)} />
        <CreateStoreSink
          open={add_sink_open}
          setOpen={setAddSinkOpen}
          selected_counter={selected_counter}
          mouse={getMouseAtZoom({ X: menu_location.X - mouse_data.offset.X, Y: menu_location.Y - mouse_data.offset.Y }, zoom)} />
        <UpdateStoreSink
          setSelectedCounter={setSelectedCounter}
          selected_sink={selected_sink}
          setSelectedSink={setSelectedSink}
          open={change_sink_open}
          setOpen={setChangeSinkOpen}
          current_sink={empty_sink} />
        <CreateStoreCooktop
          selected_counter={selected_counter}
          open={add_cooktop_open}
          setOpen={setAddCooktopOpen}
          mouse={getMouseAtZoom({ X: menu_location.X - mouse_data.offset.X, Y: menu_location.Y - mouse_data.offset.Y }, zoom)} />
        <UpdateStoreCooktop
          selected_cooktop={selected_cooktop}
          setSelectedCooktop={setSelectedCooktop}
          open={change_cooktop_open}
          setOpen={setChangeCooktopOpen} />
        <UpdateStoreOutlets
          open={outlet_cutout_open}
          setOpen={setOutletCutoutOpen}
          counter={selected_counter} />
        <UpdateStoreLabel
          open={edit_label_open}
          setOpen={setEditLabelOpen}
          selected_label={selected_label} />
      </Box>
      {
        copying && copy ?
          <Stack sx={{ position: "absolute", top: mouse_data.position.Y + 30, left: mouse_data.position.X + 20, zIndex: 2000, backgroundColor: "#fff", border: "1px solid #666666", paddingLeft: "10px", paddingRight: "10px", paddingTop: "3px", paddingBottom: "3px" }}>
            <Typography variant="body2">
              {getCopyMessage(copy)}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 10 }} color="error">
              Right click to cancel
            </Typography>
          </Stack> :
          null
      }
    </div>
  )
}
